import { isDefined, isEmpty } from "Utils/functions";
import { Form, FormikProps } from "formik";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useAdviceDetailsFormValues,
  useAdvicePriceCalculationRequests,
  useAdviceResult,
  useIsAdvicePriceLoading,
} from "./hooks/stateSelectors";

import { WrapMdMarginBottom } from "App/global-style";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { Button } from "Components/TextContext/commonComponentEnums";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { MicroAdviceProps } from "Remotes/advice/types";
import { addAdviceProductsToCartStart } from "Store/addProductToCart/slice";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { ADVICE_PRICE_STEP } from "..";
import { ProductBackendName } from "../../productTypes";
import { useExistingAdviceCart } from "../hooks";
import { AdvicePriceFormValues } from "./AdviceCards/interfaces";
import { AdviceForm } from "./AdviceForm/AdviceForm";
import { useAdviceScreenshot } from "./AdviceScreenshot/AdviceScreenshot";
import { useAdviceProductsAdviced } from "./hooks/useAdviceProductsAdviced";
import { useHandleAdviceEdit } from "./hooks/useHandleAdviceEdit";
import { useInitialFormValues } from "./hooks/useInitialFormValues";
import { getAdvicesAllowed } from "./utils";
import { validationSchema } from "./validation";

type ProductPriceMicro = Pick<MicroAdviceProps, "onAdviceCompleted">;

export const ProductPrice = ({
  onAdviceCompleted,
}: ProductPriceMicro): React.ReactElement => {
  const dispatch = useDispatch();
  const [{ formText }, lang] = useProductTexts(
    getFMSProductName(ProductBackendName.ADVICE),
  );
  const { getText } = useTexts();
  const isAdvisor = useIsAdvisorSelector();
  const addProductToCart = useSelector(
    (state: ReduxState) => state.addProductToCart,
  );
  const isPriceLoading = useIsAdvicePriceLoading();
  const advicePriceCalculationRequests = useAdvicePriceCalculationRequests();
  const adviceDetails = useAdviceDetailsFormValues();
  const adviceResult = useAdviceResult();
  const productsAdviced = useAdviceProductsAdviced();
  const initialFormValues = useInitialFormValues(isAdvisor);

  useExistingAdviceCart();
  useHandleAdviceEdit();

  const adviceScreenshot = useAdviceScreenshot();
  const { screenshotRef, takeScreenshot, setManualScreenshot } =
    adviceScreenshot;

  const doubleYearlyIncome = (adviceDetails?.income ?? 0) * 2;

  useEffect(() => {
    if (
      screenshotRef &&
      isEmpty(advicePriceCalculationRequests.changedByCustomer)
    ) {
      takeScreenshot(true);
    }
  }, [screenshotRef]);

  const handleSubmit = (
    formProps: AdvicePriceFormValues,
    options = {
      redirectToCheckout: true,
      takeScreenshot: false,
    },
  ): void => {
    const currentScreenshotRef = takeScreenshot();
    if (options.takeScreenshot) {
      setManualScreenshot(true);
    }
    dispatch(
      updatePriceCalculationFormState({
        stepId: ADVICE_PRICE_STEP.id,
        formState: formProps,
      }),
    );
    dispatch(
      addAdviceProductsToCartStart({
        formProps,
        redirectToCheckout: options.redirectToCheckout,
        takeScreenshot: options.takeScreenshot,
        screenshotRef: currentScreenshotRef,
        onAdviceCompletedForAdvisor: onAdviceCompleted,
      }),
    );
  };

  const handleTakeScreenshot = (formProps: AdvicePriceFormValues): void => {
    handleSubmit(formProps, {
      redirectToCheckout: false,
      takeScreenshot: true,
    });
  };

  if (adviceResult) {
    if (
      getAdvicesAllowed(adviceResult).length > 0 &&
      Object.keys(productsAdviced).length === 0
    ) {
      return <ThemedSpinner />;
    }
  } else {
    return <ThemedSpinner />;
  }

  if (!isDefined(initialFormValues)) {
    return <ThemedSpinner />;
  }

  return (
    <ProductPriceTile
      initialValues={initialFormValues}
      validationSchema={validationSchema(
        isAdvisor,
        formText,
        lang,
        doubleYearlyIncome,
        adviceResult,
      )}
      onSubmit={(formValues: AdvicePriceFormValues) =>
        handleSubmit(formValues, {
          redirectToCheckout: !isAdvisor,
          takeScreenshot: false,
        })
      }
      header={formText.advice.header[lang]}
      maxWidth="564px"
    >
      {(formProps: FormikProps<AdvicePriceFormValues>) => {
        return (
          <Form>
            <AdviceForm
              formProps={formProps}
              formText={formText}
              lang={lang}
              adviceScreenshot={adviceScreenshot}
              handleTakeScreenshot={handleTakeScreenshot}
            />
            {addProductToCart.error && (
              <SystemErrorAlert alertId="addProductToCart" />
            )}
            <ScrollToFirstError />
            <WrapMdMarginBottom>
              <NavigationButtons
                customNextButtonText={
                  isAdvisor ? getText(Button.SAVE_IN_CART) : undefined
                }
                isLoading={addProductToCart.isLoading}
                disableNextButton={
                  isPriceLoading || adviceScreenshot.screenshotIsLoading
                }
              />
            </WrapMdMarginBottom>
          </Form>
        );
      }}
    </ProductPriceTile>
  );
};
